import React, { useEffect, useRef } from 'react';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import Avatar from '@uikit/Avatar/Avatar';
import getFileSizeString from '@utils/getFileSizeString';
import { getDateTimeShort } from '@utils/date';
import { ThreadAttachmentType, ThreadMessageType } from '@ts/messageThread.types';
import {
  MESSAGE_THREAD_ATTACHMENT_ICON_COLOR,
  MESSAGE_THREAD_ATTACHMENT_ICON_SIZE,
  MESSAGE_THREAD_DOWNLOAD_ICON_COLOR,
  RESPONA_AUTHOR_NAME,
} from './constants';

import './MessageThread.scss';

export type MessageThreadPropsType = {
  messages: ThreadMessageType[];
  downloadingFileUid?: string;
  onDownloadAttachment?: (file: ThreadAttachmentType) => void;
  emptyMessage?: string;
};

function MessageThread({
  messages,
  downloadingFileUid = null,
  onDownloadAttachment,
  emptyMessage = 'No messages yet.',
}: MessageThreadPropsType): JSX.Element {
  const bottomAnchorRef = useRef<HTMLDivElement>(null);

  // Threads read newest-last, so land the user on the latest message rather
  // than the top of the history, and follow along as replies are sent.
  useEffect(() => {
    bottomAnchorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  if (!messages.length) {
    return <div className="message-thread message-thread--empty">{emptyMessage}</div>;
  }

  const handleAttachmentKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    file: ThreadAttachmentType
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onDownloadAttachment?.(file);
  };

  return (
    <div className="message-thread">
      {messages.map((message) => (
        <div
          key={message.id}
          className="message-thread__message"
          data-testid={`message-thread-message-${message.id}`}
          data-team={message.isResponaTeam ? 'true' : 'false'}
        >
          <Avatar
            size="s"
            wrapperClassName="message-thread__avatar-wrapper"
            className="message-thread__avatar-image"
            fullName={message.isResponaTeam ? RESPONA_AUTHOR_NAME : message.authorName}
          />
          <div className="message-thread__message-body">
            <div className="message-thread__message-header">
              <div className="message-thread__message-header__username">{message.authorName}</div>
              {message.isResponaTeam && (
                <div className="message-thread__message-header__author-type">Respona team</div>
              )}
              <div className="message-thread__message-header__date">
                {getDateTimeShort(message.createdAt)}
              </div>
            </div>
            <div
              className={`message-thread__message-container ${
                message.isResponaTeam ? 'message-thread__message-container--owner' : ''
              }`}
              data-testid={`message-thread-body-${message.id}`}
            >
              {message.body}
            </div>
            {message.attachedFiles.length > 0 && (
              <div className="message-thread__attachments">
                {message.attachedFiles.map((file) => (
                  <div
                    key={file.uid}
                    role="button"
                    tabIndex={0}
                    className={`message-thread__attachments__item ${
                      downloadingFileUid === file.uid ? 'loading' : ''
                    }`}
                    title={file.name}
                    onClick={() => onDownloadAttachment?.(file)}
                    onKeyDown={(event) => handleAttachmentKeyDown(event, file)}
                  >
                    <SVGIcon
                      icon="fileBlank"
                      color={MESSAGE_THREAD_ATTACHMENT_ICON_COLOR}
                      size={MESSAGE_THREAD_ATTACHMENT_ICON_SIZE}
                    />
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{getFileSizeString(file.size)}</span>
                    <SVGIcon
                      icon="download"
                      color={MESSAGE_THREAD_DOWNLOAD_ICON_COLOR}
                      size={MESSAGE_THREAD_ATTACHMENT_ICON_SIZE}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      <div ref={bottomAnchorRef} />
    </div>
  );
}

export default MessageThread;
